/**
 * Turning a completeness gap into an actual add.
 *
 * ⚠️ THE INCOHERENCE THIS RESOLVES. "Make it a complete trip" was built as a
 * QUESTIONNAIRE: each element offered one-tap answers, and answering marked it
 * resolved. But answering writes a completeness record — it does NOT put
 * anything on the itinerary. So a traveler could tap "renting a car", watch the
 * rail congratulate them with "Your trip is fully mapped", and still have no
 * car, no hotel and a seven-night hole where the cruise goes.
 *
 * Once Steward could actually FIND the thing, two rival definitions of
 * "complete" were live at once: *you answered the question* and *the thing is
 * on your itinerary*. Only the second one is true. This module is the bridge —
 * every gap the rail knows about maps onto the add flow, so the rail becomes
 * the entry point to filling it rather than a parallel checklist that can
 * disagree with the itinerary.
 *
 * Pure, so the mapping is assertable without a DOM.
 */
import type { CompletenessElement, CompletenessElementKey } from "@/lib/api";
import type { ItineraryResponse, TripPlanResponse } from "@/lib/types";
import type { AddContext } from "@/components/trips/itinerary/addToItinerary-utils";

/** What the add sheet should open pre-selected for a given gap. */
export interface PlanTarget {
  /** An `AddChoice.key` from the add catalogue. */
  choiceKey: string;
  context: AddContext;
}

/**
 * Which tile each gap opens on. A rental car is the honest default for the
 * two airport-transport gaps on a trip that flies: it is the option travelers
 * pick most and the only one we can price live. "Transfer" stays one tap away
 * in the same sheet, so this is a starting point, not a decision made for them.
 */
const CHOICE_FOR: Record<CompletenessElementKey, string> = {
  depart_transport: "transfer",
  arrival_transfer: "car",
  lodging: "hotel",
  activities: "activity",
  return_transfer: "transfer",
  home_transport: "transfer",
};

/** Where in the trip each gap sits, in words Steward can reason about. */
function positionFor(key: CompletenessElementKey, plan: TripPlanResponse | null): string | null {
  const outbound = plan?.legs.find((l) => l.role === "outbound") ?? plan?.legs[0] ?? null;
  const ret = plan?.legs.find((l) => l.role === "return") ?? null;
  switch (key) {
    case "depart_transport":
      return outbound?.origin ? `before your flight out of ${outbound.origin}` : null;
    case "arrival_transfer":
      return outbound?.destination ? `right after you land at ${outbound.destination}` : null;
    case "return_transfer":
      return ret?.origin ? `getting to ${ret.origin} for the flight home` : null;
    case "home_transport":
      return ret?.destination ? `after you land back at ${ret.destination}` : null;
    default:
      return null;
  }
}

/** The day and city each gap belongs to, from the trip's real legs. */
function whereWhen(
  key: CompletenessElementKey,
  plan: TripPlanResponse | null,
): { date: string | null; city: string | null } {
  const outbound = plan?.legs.find((l) => l.role === "outbound") ?? plan?.legs[0] ?? null;
  const ret = plan?.legs.find((l) => l.role === "return") ?? null;
  switch (key) {
    case "depart_transport":
      return { date: outbound?.date ?? null, city: outbound?.originCity ?? null };
    case "arrival_transfer":
      return { date: outbound?.date ?? null, city: outbound?.destinationCity ?? null };
    case "return_transfer":
      return { date: ret?.date ?? null, city: ret?.originCity ?? null };
    case "home_transport":
      return { date: ret?.date ?? null, city: ret?.destinationCity ?? null };
    // Lodging and activities belong to the stay, not to a flight — the sheet's
    // own day and place pickers are the right place to narrow them.
    default:
      return { date: null, city: ret?.originCity ?? outbound?.destinationCity ?? null };
  }
}

export function planTargetFor(
  el: CompletenessElement,
  plan: TripPlanResponse | null,
): PlanTarget {
  const { date, city } = whereWhen(el.key, plan);
  return {
    choiceKey: CHOICE_FOR[el.key] ?? "activity",
    context: { date, city, position: positionFor(el.key, plan) },
  };
}

/**
 * Itinerary item types that would genuinely satisfy each gap.
 *
 * Used for the honesty check below — NOT to auto-resolve anything. Marking a
 * gap done because an item looks related is the same overreach in the other
 * direction.
 */
const SATISFIED_BY: Record<CompletenessElementKey, string[]> = {
  depart_transport: ["transfer"],
  arrival_transfer: ["transfer"],
  lodging: ["lodging"],
  activities: ["activity", "event"],
  return_transfer: ["transfer"],
  home_transport: ["transfer"],
};

/**
 * Is there anything on the itinerary that actually covers this gap?
 *
 * ⚠️ THIS IS THE HONESTY CHECK. An element the traveler ANSWERED still shows
 * "done", because dismissing their answer would be rude and wrong — they may
 * have booked it elsewhere. But when they answered and nothing was ever added,
 * the rail says so quietly instead of letting "fully mapped" stand over an
 * empty itinerary.
 */
export function hasMatchingItem(
  el: CompletenessElement,
  itinerary: ItineraryResponse | null | undefined,
): boolean {
  const want = SATISFIED_BY[el.key] ?? [];
  if (want.length === 0) return false;
  for (const day of itinerary?.days ?? []) {
    for (const seg of day.segments) {
      if (seg.kind === "item" && want.includes(seg.item_type)) return true;
    }
  }
  return false;
}
